/**
 * 开户 / KYC 埋点 helpers（规范 4.2）。
 */

import { isInAppWebView } from './appInfo'
import { track } from './track'

export const KYC_EVENTS = {
  account_open_start: 'account_open_start',
  kyc_step_view: 'kyc_step_view',
  kyc_step_complete: 'kyc_step_complete',
  kyc_id_ocr_result: 'kyc_id_ocr_result',
  kyc_risk_answer: 'kyc_risk_answer',
  account_open_submit: 'account_open_submit'
} as const

export type KycStepCode =
  | 'identity_select'
  | 'id_upload'
  | 'risk'
  | 'liveness'
  | 'signature'
  | 'submit'

const PAGE_ACCOUNT_OPEN = 'page_account_open'

const STEP_INDEX: Record<KycStepCode, number> = {
  identity_select: 0,
  id_upload: 1,
  risk: 2,
  liveness: 3,
  signature: 4,
  submit: 5
}

export function getKycStepIndex(stepCode: KycStepCode): number {
  return STEP_INDEX[stepCode]
}

/** App WebView 内由原生打 start；独立浏览器才打 */
export function trackAccountOpenStart(props?: {
  entry?: string
  user_type?: string
}): void {
  if (isInAppWebView()) return
  track(KYC_EVENTS.account_open_start, {
    page_id: PAGE_ACCOUNT_OPEN,
    entry: props?.entry ?? 'h5_web',
    user_type: props?.user_type ?? 'personal',
    client_env: 'h5'
  })
}

export function trackKycStepView(props: { step_code: KycStepCode; step_index?: number }): void {
  track(KYC_EVENTS.kyc_step_view, {
    page_id: PAGE_ACCOUNT_OPEN,
    step_code: props.step_code,
    step_index: props.step_index ?? getKycStepIndex(props.step_code),
    client_env: 'h5'
  })
}

export function trackKycStepComplete(props: {
  step_code: KycStepCode
  spend_time_ms: number
  is_success: boolean
}): void {
  track(KYC_EVENTS.kyc_step_complete, {
    page_id: PAGE_ACCOUNT_OPEN,
    step_code: props.step_code,
    step_index: getKycStepIndex(props.step_code),
    spend_time_ms: Math.max(0, Math.round(props.spend_time_ms)),
    is_success: props.is_success,
    client_env: 'h5'
  })
}

export function trackKycIdOcrResult(props: {
  doc_type: 'ID' | 'passport' | string
  result: 'success' | 'fail' | string
  fail_reason?: string
}): void {
  const payload: Record<string, unknown> = {
    page_id: PAGE_ACCOUNT_OPEN,
    doc_type: props.doc_type,
    result: props.result,
    client_env: 'h5'
  }
  if (props.fail_reason?.trim()) payload.fail_reason = props.fail_reason.trim()
  track(KYC_EVENTS.kyc_id_ocr_result, payload)
}

export function trackKycRiskAnswer(props: {
  question_id: string | number
  selected_option: string | number
}): void {
  track(KYC_EVENTS.kyc_risk_answer, {
    page_id: PAGE_ACCOUNT_OPEN,
    question_id: props.question_id,
    selected_option: props.selected_option,
    client_env: 'h5'
  })
}

export function trackAccountOpenSubmit(props?: { has_pro_investor?: boolean }): void {
  const payload: Record<string, unknown> = {
    page_id: PAGE_ACCOUNT_OPEN,
    client_env: 'h5'
  }
  if (typeof props?.has_pro_investor === 'boolean') {
    payload.has_pro_investor = props.has_pro_investor
  }
  track(KYC_EVENTS.account_open_submit, payload)
}
